import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from './supabase';
import { buildAuthRedirectPath } from './utils/authRedirect';

export default function StudentLoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [status, setStatus] = useState('Login');

    useEffect(() => {
        const checkSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session && localStorage.getItem('userRole') === 'student') {
                navigate('/student-dashboard', { replace: true });
            }
        };
        checkSession();
    }, [navigate]);

    const handleGoogleSignIn = async () => {
        setLoading(true);
        setStatus('Authenticating...');
        setError('');
        try {
            localStorage.setItem('pendingOAuthIntent', 'google-auth');
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: buildAuthRedirectPath('/google-signup'),
                },
            });
            if (error) throw error;
        } catch (error: any) {
            console.error('Google Sign-In Error:', error);
            setError(`Google Sign-In failed: ${error.message}`);
            setStatus('Login');
            setLoading(false);
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);
        setStatus('Authenticating...');
        setError('');

        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) throw error;

            // Verify this user has a student record
            const { data: studentData } = await supabase
                .from('students')
                .select('id')
                .eq('user_id', data.user.id)
                .maybeSingle();

            if (!studentData) {
                const { data: teacherData } = await supabase
                    .from('teachers')
                    .select('id')
                    .eq('user_id', data.user.id)
                    .maybeSingle();

                if (teacherData) {
                    await supabase.auth.signOut();
                    setError('This email is registered as a Teacher account. Please use Teacher Login instead.');
                    setStatus('Login');
                    setLoading(false);
                    return;
                }

                // No student record and no teacher record — don't allow login
                await supabase.auth.signOut();
                setError('No student account found for this email. Please register as a student first.');
                setStatus('Login');
                setLoading(false);
                return;
            }

            localStorage.setItem('userRole', 'student');
            localStorage.setItem('userEmail', email);
            setStatus('Redirecting...');
            navigate('/student-dashboard');
        } catch (error: any) {
            console.error('Login error:', error);
            let errorMessage = 'Login failed. Please try again.';

            if (error.message?.includes('Invalid login credentials')) {
                errorMessage = 'Incorrect password or no account found. If you signed up with Google, use the "Sign in with Google" button below.';
            } else if (error.message?.includes('Email not confirmed')) {
                errorMessage = 'Please confirm your email address first.';
            } else {
                errorMessage = `Login failed: ${error.message}`;
            }

            setError(errorMessage);
            setStatus('Login');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page-shell">
            <div className="page-panel auth-card" style={{ boxShadow: '0 20px 55px rgba(15,23,42,0.08)', border: '1px solid rgba(148,163,184,0.16)' }}>
                <div className="auth-header">
                    <div className="auth-icon-badge" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>S</div>
                    <p className="hero-eyebrow" style={{ display: 'inline-block', background: 'rgba(16,185,129,0.1)', color: '#059669', borderColor: 'rgba(16,185,129,0.15)' }}>Welcome Back Student</p>
                    <h1 className="page-title" style={{ textAlign: 'center', marginBottom: '0.75rem' }}>Student Login</h1>
                    <p className="auth-subcopy">Return to your learning space, revisit saved activity, and continue finding the right teachers.</p>
                </div>

                <form className="auth-form" onSubmit={handleSubmit}>
                    {error && <div className="auth-feedback error">{error}</div>}
                    <input
                        type="email"
                        id="student-login-email"
                        name="email"
                        className="input-field"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                    <input
                        type="password"
                        id="student-login-password"
                        name="password"
                        className="input-field"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />
                    <div className="auth-inline-link-row">
                        <Link to="/forgot-password" style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textDecoration: 'none' }}>
                            Forgot Password?
                        </Link>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="btn primary" disabled={loading}>
                            {loading ? status : 'Login'}
                        </button>

                        <button type="button" className="btn secondary" onClick={handleGoogleSignIn} disabled={loading} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                            </svg>
                            Sign in with Google
                        </button>

                        <p className="form-note">
                            Don't have an account? <Link to="/student-signup" className="page-link">Sign up</Link>
                        </p>
                        <p className="form-note">
                            Are you a teacher? <Link to="/login" className="page-link">Teacher Login</Link>
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
}
